import { graphql } from "babel-plugin-relay/macro";
import { useCallback, useContext } from "react";
import { useMutation } from "react-relay";
import { UploadableMap } from "relay-runtime";
import type { useScrapeAndSaveBrandStyleMutation as useScrapeAndSaveBrandStyleMutationType } from "./__generated__/useScrapeAndSaveBrandStyleMutation.graphql";
import SnackbarContext from "../contexts/SnackbarContext";

const mutation = graphql`
  mutation useScrapeAndSaveBrandStyleMutation($brandId: ID!) {
    scrapeAndSaveBrandStyle(brandId: $brandId) {
      brand {
        id
        ...InternBrandStylePalette_brand
        ...InternBrandStyleLogo_brand
        ...InternBrandStyleTypography_brand
      }
      errors {
        message
        type
      }
    }
  }
`;

const useScrapeAndSaveBrandStyleMutation = (): [
  (
    brandId: string,
    onCompleted?: () => void,
    uploadables?: UploadableMap,
    updater?: (store: any) => void
  ) => void,
  boolean
] => {
  const [saveMutation, isMutationInFlight] =
    useMutation<useScrapeAndSaveBrandStyleMutationType>(mutation);
  const snackbarContext = useContext(SnackbarContext);

  const onMutate = useCallback(
    (
      brandId: string,
      onCompleted?: () => void,
      uploadables?: UploadableMap,
      updater?: (store: any) => void
    ) => {
      saveMutation({
        variables: {
          brandId,
        },
        onCompleted: (data, errors) => {
          if (errors) {
            snackbarContext?.openSnackbar(
              "Failed to scrape and save brand style",
              "error"
            );
          } else {
            const result = data?.scrapeAndSaveBrandStyle;
            if (result?.errors && result.errors.length > 0) {
              const errorMessage = result.errors
                .map((e) => e.message)
                .join(", ");
              snackbarContext?.openSnackbar(
                `Scraping failed: ${errorMessage}`,
                "error"
              );
            } else {
              onCompleted && onCompleted();
              snackbarContext?.openSnackbar(
                "Brand style scraped and saved successfully",
                "success"
              );
            }
          }
        },
        onError: (error: Error) => {
          snackbarContext?.openSnackbar(
            "Failed to scrape and save brand style",
            "error"
          );
        },
        updater,
        uploadables,
      });
    },
    [snackbarContext, saveMutation]
  );

  return [onMutate, isMutationInFlight];
};

export default useScrapeAndSaveBrandStyleMutation;
